import type { RuntimeConfig } from './auth/oauth';
import { beginSignIn, getTokens, refreshNow } from './auth/session';
import type { Row, TableName } from './table';

export interface TableResult {
  generatedAt: string;
  summary?: Record<string, unknown>;
  rows: Row[];
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function errorFrom(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
  return new ApiError(res.status, body.error?.code ?? 'HTTP_ERROR', body.error?.message ?? `HTTP ${res.status}`);
}

/** GET /api/config, which works before sign-in. A 503 carries the setup step still to do. */
export async function loadConfig(): Promise<RuntimeConfig> {
  const res = await fetch('/api/config');
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as RuntimeConfig;
}

export async function getTable(config: RuntimeConfig, table: TableName): Promise<TableResult> {
  const call = () => fetch(`/api/results/${table}`, {
    headers: { authorization: `Bearer ${getTokens()?.idToken ?? ''}` },
  });
  let res = await call();
  if (res.status === 401 && getTokens()?.refreshToken) {
    await refreshNow(config).catch(() => undefined);
    res = await call();
  }
  if (res.status === 401) {
    await beginSignIn(config, window.location.pathname);
    throw new ApiError(401, 'UNAUTHORIZED', 'Signing in again...');
  }
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as TableResult;
}
